import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';
import { toast } from 'react-toastify';

const ComposeEmailDialog = ({ isOpen, onClose }) => {
  const [masterDataList, setMasterDataList] = useState([]);
  const [selectedMasterData, setSelectedMasterData] = useState(null);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [manualEmails, setManualEmails] = useState([]);
  const [showAddEmailInput, setShowAddEmailInput] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchMasterData();
    }
  }, [isOpen]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/master-data');
      const data = response.data.success ? response.data.data : response.data;
      setMasterDataList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
      toast.error('Failed to fetch master data');
    } finally {
      setLoading(false);
    }
  };

  const filteredMasterData = masterDataList.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.clientName && item.clientName.toLowerCase().includes(searchLower)) ||
      (item.companyName && item.companyName.toLowerCase().includes(searchLower)) ||
      (item.customId && item.customId.toLowerCase().includes(searchLower))
    );
  });

  const handleMasterDataSelect = (masterData) => {
    setSelectedMasterData(masterData);
  };

  const getContactPersonEmails = () => {
    if (!selectedMasterData || !selectedMasterData.contactPersons) {
      return [];
    }
    return selectedMasterData.contactPersons
      .filter(cp => cp.email && cp.email.trim())
      .map(cp => cp.email.trim());
  };

  const getAllRecipientEmails = () => {
    const contactEmails = getContactPersonEmails();
    const manualEmailList = manualEmails.map(e => e.email.trim());
    return [...contactEmails, ...manualEmailList];
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const email = newEmailInput.trim();
    
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check if email already exists
    const allEmails = getAllRecipientEmails();
    if (allEmails.includes(email.toLowerCase())) {
      toast.error('This email address is already added');
      return;
    }

    setManualEmails([...manualEmails, { email: email, id: Date.now() }]);
    setNewEmailInput('');
    setShowAddEmailInput(false);
  };

  const handleRemoveManualEmail = (id) => {
    setManualEmails(manualEmails.filter(e => e.id !== id));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddEmail();
    } else if (e.key === 'Escape') {
      setShowAddEmailInput(false);
      setNewEmailInput('');
    }
  };

  const handleSend = async () => {
    if (!selectedMasterData) {
      toast.error('Please select a master data');
      return;
    }
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!content.trim()) {
      toast.error('Please enter email content');
      return;
    }

    // Get all recipient emails (contact persons + manually added)
    const recipientEmails = getAllRecipientEmails();
    
    if (recipientEmails.length === 0) {
      toast.error('Please add at least one email address (select master data or add manually)');
      return;
    }

    try {
      setSending(true);
      
      // Send single email to all recipients
      await api.post('/email/send', {
        emails: recipientEmails,
        subject: subject,
        message: content
      });

      toast.success(`Email sent successfully to ${recipientEmails.length} recipient(s)`);
      
      // Reset form
      setSelectedMasterData(null);
      setSubject('');
      setContent('');
      setSearchTerm('');
      setManualEmails([]);
      setShowAddEmailInput(false);
      setNewEmailInput('');
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSelectedMasterData(null);
    setSubject('');
    setContent('');
    setSearchTerm('');
    setManualEmails([]);
    setShowAddEmailInput(false);
    setNewEmailInput('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Compose Email</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left Panel - Master Data Selection */}
          <div className="w-1/3 border-r bg-gray-50 flex flex-col min-h-0">
            <div className="p-4 border-b bg-white flex-shrink-0">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Select Master Data</h3>
              <input
                type="text"
                placeholder="Search master data..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-2 min-h-0">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : filteredMasterData.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-8">
                  {searchTerm ? 'No master data found' : 'No master data available'}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredMasterData.map((item) => (
                    <div
                      key={item._id}
                      onClick={() => handleMasterDataSelect(item)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedMasterData?._id === item._id
                          ? 'bg-blue-100 border-2 border-blue-500'
                          : 'bg-white border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-900">
                        {item.clientName || item.companyName || 'Unnamed'}
                      </div>
                      {item.companyName && item.clientName && (
                        <div className="text-xs text-gray-600 mt-1">{item.companyName}</div>
                      )}
                      {item.contactPersons && item.contactPersons.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {item.contactPersons.filter(cp => cp.email).length} contact person{item.contactPersons.filter(cp => cp.email).length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Email Editor */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="p-6 border-b bg-white flex-shrink-0">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      To {getAllRecipientEmails().length > 0 && (
                        <span className="text-xs text-gray-500 font-normal">
                          ({getAllRecipientEmails().length} recipient{getAllRecipientEmails().length !== 1 ? 's' : ''})
                        </span>
                      )}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowAddEmailInput(true)}
                      className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Add Email</span>
                    </button>
                  </div>
                  
                  <div className="w-full min-h-[42px] px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 max-h-32 overflow-y-auto">
                    {getAllRecipientEmails().length > 0 ? (
                      <div className="space-y-2">
                        {/* Contact Persons */}
                        {selectedMasterData && selectedMasterData.contactPersons
                          .filter(cp => cp.email && cp.email.trim())
                          .map((cp, index) => (
                            <div key={`cp-${index}`} className="flex items-center justify-between text-sm bg-white px-2 py-1 rounded border border-gray-200">
                              <span>{cp.title} {cp.name} &lt;{cp.email}&gt;</span>
                              <span className="text-xs text-gray-400">Contact Person</span>
                            </div>
                          ))}
                        
                        {/* Manually Added Emails */}
                        {manualEmails.map((item) => (
                          <div key={item.id} className="flex items-center justify-between text-sm bg-white px-2 py-1 rounded border border-gray-200">
                            <span>{item.email}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveManualEmail(item.id)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                              title="Remove email"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400">
                        {selectedMasterData 
                          ? 'No contact persons with email addresses. Click "+ Add Email" to add manually.'
                          : 'Select master data or click "+ Add Email" to add email addresses'}
                      </div>
                    )}
                  </div>

                  {/* Add Email Input */}
                  {showAddEmailInput && (
                    <div className="mt-2 flex items-center space-x-2">
                      <input
                        type="email"
                        value={newEmailInput}
                        onChange={(e) => setNewEmailInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Enter email address"
                        className="flex-1 px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleAddEmail}
                        className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddEmailInput(false);
                          setNewEmailInput('');
                        }}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email subject"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col min-h-0">
              <div className="p-4 border-b bg-white flex-shrink-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
              </div>
              <div className="flex-1 min-h-0 relative" style={{ height: '400px' }}>
                <ReactQuill
                  value={content}
                  onChange={setContent}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'align': [] }],
                      ['link'],
                      ['clean']
                    ]
                  }}
                  placeholder="Write your email content here..."
                  style={{
                    height: '100%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-3"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !subject.trim() || !content.trim() || getAllRecipientEmails().length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComposeEmailDialog;

